/**
 * Priority queue — the "who to chase next" list.
 *
 * Same building blocks as the Expirations tab, so the two read as one screen: the filter row is
 * the golden `ContractorsHeader` shape, the grid is the shared `GridTable`, and every cell reuses
 * an existing chip. What differs is the intent — rows are ordered by how soon they expire, and the
 * last-contact column plus its filter exist so nobody gets chased twice in a day.
 *
 * Search, filters, sort and pagination are all server-side (`GET .../priority-queue`), the same
 * convention `ContractorsTable` uses — every count agrees with what the backend actually holds.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid-pro'
import { GSIcon } from 'src/components/shared/GSIcon'
import { colors } from 'src/theme'
import {
  CreatePriorityQueueContact,
  ExportPriorityQueueCsv,
  FetchPriorityQueue,
  FetchPriorityQueueDetail,
  FetchPriorityQueueFilterOptions,
} from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { handleApiError } from 'src/utils/errorHandlers'
import { downloadFileFromBuffer } from 'src/utils/fileDownload'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { GridColumnsSelect } from 'src/components/shared/grid/grid-columns-select/GridColumnsSelect'
import { ExportDataModal } from 'src/components/shared/grid/export-data-modal/ExportDataModal'
import { TableSearch } from 'src/components/shared/table/TableSearch'
import { TableSelect } from 'src/components/shared/table/TableSelect'
import { LimitedStringChip } from 'src/components/shared/LimitedStringChip'
import { TableTags } from 'src/components/tags/TableTags'
import { ContractorStatusChip } from 'src/components/contractor-list/shared/ContractorStatusChip'
import { ContractorStatus } from 'src/constants/contractor/enums'
import { contractorStatusTitles } from 'src/constants/contractor/constants'
import type { ExportPriorityQueueCsvPayload } from 'src/sagas/monitoring.types'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'
import { useGridColumnPreferences } from 'src/utils/hooks/grid/useGridColumnPreferences'
import { ContactContractorDrawer } from './ContactContractorDrawer'
import { ExpirationIndicatorChip } from './ExpirationIndicatorChip'
import { ExpirationsEmptyState } from './ExpirationsEmptyState'
import { LastContactCell } from './LastContactCell'
import { getDaysAgo, getDaysLeft, getDisplayStatus } from './utils'
import {
  Contact,
  ContactChannel,
  ExcludeContactedWindow,
  ExpirationIndicator,
  PriorityQueueDetail,
  PriorityQueueFilterOptions,
  PriorityQueueGridRow,
  PriorityQueueRow,
} from './types'

const LOCKED_COLUMNS = ['contractorId', 'name']
const PAGE_SIZE_OPTIONS = [25, 50, 100]

/** Grid column field → the backend's `PriorityQueueSortColumns` key, for the `sort` query param. */
const SORT_QUERY_KEY: Record<string, string> = {
  contractorId: 'contractorId',
  name: 'name',
  contractorStatus: 'contractorStatus',
  daysLeft: 'expiresIn',
  lastExternalContact: 'lastExternalContactAt',
}

/**
 * Grid column field → the backend's `PriorityQueueExportColumnKey`, for the CSV export. A superset
 * of `SORT_QUERY_KEY`: tags, phone number and onboarding flow can be exported but not sorted by.
 * A field missing here cannot be exported, so it is dropped from the export selection.
 */
const EXPORT_COLUMN_KEY: Record<string, string> = {
  contractorId: 'contractorId',
  name: 'name',
  contractorStatus: 'contractorStatus',
  tags: 'tags',
  daysLeft: 'expiresIn',
  phoneNumber: 'phoneNumber',
  onboardingFlow: 'onboardingFlow',
  lastExternalContact: 'lastExternalContactAt',
}

/**
 * Mirrors `ContractorExpirationSelect` upstream — same wording, same chips. "More than 30 days"
 * is left out: the queue only ever holds contractors with an expiring-or-expired document, so that
 * bucket can never match a row, and the backend rejects it as a filter value.
 */
const EXPIRATION_FILTER_LABELS: Record<string, string> = {
  [ExpirationIndicator.ExpiringSoon]: '30 days and less',
  [ExpirationIndicator.Expired]: 'Expired',
}

const EXPIRATION_OPTIONS = [
  {
    value: ExpirationIndicator.ExpiringSoon,
    component: (
      <ExpirationIndicatorChip
        indicator={ExpirationIndicator.ExpiringSoon}
        label={EXPIRATION_FILTER_LABELS[ExpirationIndicator.ExpiringSoon]}
      />
    ),
  },
  {
    value: ExpirationIndicator.Expired,
    component: (
      <ExpirationIndicatorChip
        indicator={ExpirationIndicator.Expired}
        label={EXPIRATION_FILTER_LABELS[ExpirationIndicator.Expired]}
      />
    ),
  },
]

const DEFAULT_EXPIRATION_FILTER: ExpirationIndicator[] = [ExpirationIndicator.ExpiringSoon]

/**
 * Only the two states the queue distinguishes. `expires_soon` is not offered: the backend already
 * collapses it into `active` (`contractorStatus` filters on `isActive`, not the derived status),
 * so selecting Active already includes an expiring-soon contractor.
 */
const STATUS_OPTIONS = [
  {
    value: ContractorStatus.Active,
    component: <ContractorStatusChip status={ContractorStatus.Active} />,
  },
  {
    value: ContractorStatus.Deactivated,
    component: <ContractorStatusChip status={ContractorStatus.Deactivated} />,
  },
]

/**
 * Windows offered by the "Exclude contacted" filter. Values match the backend's
 * `ExcludeContactedWindow` enum directly — inclusive of their last day, so "Contacted in 3 days"
 * also hides someone reached exactly 3 days ago, and "Contacted today" hides only today's contacts.
 */
const CONTACT_WINDOWS: Array<{ value: ExcludeContactedWindow; label: string }> = [
  { value: ExcludeContactedWindow.Today, label: 'Contacted today' },
  { value: ExcludeContactedWindow.ThreeDays, label: 'Contacted in 3 days' },
  { value: ExcludeContactedWindow.SevenDays, label: 'Contacted in 7 days' },
  { value: ExcludeContactedWindow.FourteenDays, label: 'Contacted in 14 days' },
]

const toGridRow = (row: PriorityQueueRow): PriorityQueueGridRow => ({
  ...row,
  daysLeft: getDaysLeft(row.expiresAt),
})

const PriorityQueueComponent = () => {
  const dispatch = useDispatch()

  const [rows, setRows] = useState<PriorityQueueGridRow[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)

  const [filterOptions, setFilterOptions] = useState<PriorityQueueFilterOptions>({
    templates: [],
    tags: [],
  })

  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<ContractorStatus[]>([])
  const [flowFilter, setFlowFilter] = useState<string[]>([])
  const [tagFilter, setTagFilter] = useState<string[]>([])
  const [expirationFilter, setExpirationFilter] =
    useState<ExpirationIndicator[]>(DEFAULT_EXPIRATION_FILTER)
  const [excludeWindows, setExcludeWindows] = useState<ExcludeContactedWindow[]>([])
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'daysLeft', sort: 'asc' }])

  const { paginationModel, limit, offset, setPaginationModel } =
    useGridPagination(PAGE_SIZE_OPTIONS)

  const [openedId, setOpenedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<PriorityQueueDetail | null>(null)
  const detailRequestRef = useRef(0)

  const fetchFilterOptions = useCallback(async () => {
    try {
      const options: PriorityQueueFilterOptions = await createAsyncAction(
        dispatch,
        FetchPriorityQueueFilterOptions.request(),
      )

      setFilterOptions(options)
    } catch (err: unknown) {
      handleApiError(err)
    }
  }, [dispatch])

  useEffect(() => {
    fetchFilterOptions()
  }, [fetchFilterOptions])

  const fetchRows = useCallback(
    async ({
      limit,
      offset,
      searchQuery,
      sortModel,
      statusFilter,
      flowFilter,
      tagFilter,
      expirationFilter,
      excludeWindows,
    }: {
      limit: number
      offset: number
      searchQuery: string
      sortModel: GridSortModel
      statusFilter: ContractorStatus[]
      flowFilter: string[]
      tagFilter: string[]
      expirationFilter: ExpirationIndicator[]
      excludeWindows: ExcludeContactedWindow[]
    }) => {
      setIsLoading(true)
      setHasError(false)

      try {
        const params: {
          limit: number
          offset: number
          search?: string
          contractorStatus?: ContractorStatus[]
          collectDataTemplateIds?: string[]
          tags?: string[]
          expirationIndicator?: ExpirationIndicator[]
          excludeContactedWithin?: ExcludeContactedWindow
          sort?: string
        } = { limit, offset }

        if (searchQuery) {
          params.search = searchQuery
        }

        if (statusFilter.length) {
          params.contractorStatus = statusFilter
        }

        if (flowFilter.length) {
          params.collectDataTemplateIds = flowFilter
        }

        if (tagFilter.length) {
          params.tags = tagFilter
        }

        if (expirationFilter.length) {
          params.expirationIndicator = expirationFilter
        }

        if (excludeWindows[0]) {
          params.excludeContactedWithin = excludeWindows[0]
        }

        if (sortModel[0]) {
          params.sort = `${SORT_QUERY_KEY[sortModel[0].field]}:${sortModel[0].sort?.toUpperCase()}`
        }

        const response: { data: PriorityQueueRow[]; pagination: { total: number } } =
          await createAsyncAction(dispatch, FetchPriorityQueue.request(params))

        setRows(response.data.map(toGridRow))
        setTotal(response.pagination.total)
      } catch (err: unknown) {
        handleApiError(err)
        setRows([])
        setTotal(0)
        setHasError(true)
      }

      setIsLoading(false)
    },
    [dispatch],
  )

  /** The current page, with the filters and sort exactly as they are now. */
  const refreshRows = useCallback(() => {
    fetchRows({
      limit,
      offset,
      searchQuery,
      sortModel,
      statusFilter,
      flowFilter,
      tagFilter,
      expirationFilter,
      excludeWindows,
    })
  }, [
    fetchRows,
    limit,
    offset,
    searchQuery,
    sortModel,
    statusFilter,
    flowFilter,
    tagFilter,
    expirationFilter,
    excludeWindows,
  ])

  useEffect(() => {
    refreshRows()
  }, [refreshRows])

  const fetchDetail = useCallback(
    async (contractorId: string, requestId: number) => {
      try {
        const result: PriorityQueueDetail = await createAsyncAction(
          dispatch,
          FetchPriorityQueueDetail.request({ contractorId }),
        )

        if (detailRequestRef.current === requestId) {
          setDetail(result)
        }
      } catch (err: unknown) {
        if (detailRequestRef.current === requestId) {
          handleApiError(err)
          setOpenedId(null)
        }
      }
    },
    [dispatch],
  )

  /**
   * Invalidates any in-flight detail request and clears the drawer in the same tick. The effect
   * below does the same for the `openedId` transition, but it runs a render later — long enough for
   * a response to resolve, still match its own `requestId`, and reopen the drawer the user closed.
   */
  const handleCloseDrawer = useCallback(() => {
    detailRequestRef.current += 1
    setDetail(null)
    setOpenedId(null)
  }, [])

  useEffect(() => {
    if (!openedId) {
      detailRequestRef.current += 1
      setDetail(null)

      return
    }

    const requestId = ++detailRequestRef.current

    fetchDetail(openedId, requestId)
  }, [openedId, fetchDetail])

  const handleLogContact = useCallback(
    async (contractorId: string, channel: ContactChannel, note?: string) => {
      try {
        const created: Contact = await createAsyncAction(
          dispatch,
          CreatePriorityQueueContact.request({ contractorId, channel, note }),
        )

        /**
         * Refetch rather than patch the row. A logged contact can change which contractors the
         * server returns at all ("exclude contacted" drops this one) and where they sort (a sort on
         * last contact moves it), so a local edit leaves the page inconsistent with `total` and can
         * strand a row on a page that no longer exists. The drawer reads `detail`, not `rows`, so
         * refetching does not disturb what the user is looking at.
         */
        refreshRows()

        setDetail((current) =>
          current && current.id === contractorId
            ? { ...current, contacts: [created, ...current.contacts] }
            : current,
        )
      } catch (err: unknown) {
        handleApiError(err)

        throw err
      }
    },
    [dispatch, refreshRows],
  )

  const resetPage = useCallback(() => {
    setPaginationModel((model) => ({ ...model, page: 0 }))
  }, [setPaginationModel])

  const handleChangeSearchQuery = useCallback(
    (value: string) => {
      setSearchQuery(value)
      resetPage()
    },
    [resetPage],
  )

  /**
   * Every filter resets the page: a narrower result set can be shorter than the page the user is
   * standing on, and an empty grid then reads as "no matches" rather than "wrong page".
   */
  const handleChangeStatusFilter = useCallback(
    (value: ContractorStatus[]) => {
      setStatusFilter(value)
      resetPage()
    },
    [resetPage],
  )

  const handleChangeFlowFilter = useCallback(
    (value: string[]) => {
      setFlowFilter(value)
      resetPage()
    },
    [resetPage],
  )

  const handleChangeTagFilter = useCallback(
    (value: string[]) => {
      setTagFilter(value)
      resetPage()
    },
    [resetPage],
  )

  const handleChangeExpirationFilter = useCallback(
    (value: ExpirationIndicator[]) => {
      setExpirationFilter(value)
      resetPage()
    },
    [resetPage],
  )

  const handleChangeExcludeWindows = useCallback(
    (value: ExcludeContactedWindow[]) => {
      setExcludeWindows(value)
      resetPage()
    },
    [resetPage],
  )

  const handleSortModelChange = useCallback((value: GridSortModel) => {
    setSortModel(value)
  }, [])

  const handleChangePagination = useCallback(
    (value: GridPaginationModel) => {
      setPaginationModel(value)
    },
    [setPaginationModel],
  )

  const handleOpenExportModal = useCallback(() => {
    setIsExportModalOpen(true)
  }, [])

  const handleCloseExportModal = useCallback(() => {
    setIsExportModalOpen(false)
  }, [])

  const columns = useMemo<Array<GridColDef<PriorityQueueGridRow>>>(
    () => [
      {
        field: 'contractorId',
        headerName: 'Contractor ID',
        flex: 2,
        sortable: true,
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 3,
        sortable: true,
      },
      {
        field: 'contractorStatus',
        headerName: 'Contractor status',
        flex: 3,
        sortable: true,
        renderCell: (params: GridRenderCellParams<PriorityQueueGridRow>) => (
          <ContractorStatusChip status={getDisplayStatus(params.row.contractorStatus)} />
        ),
      },
      {
        field: 'tags',
        headerName: 'Tags',
        flex: 3,
        sortable: false,
        renderCell: (params: GridRenderCellParams<PriorityQueueGridRow>) =>
          params.row.tags.length ? (
            <TableTags tags={params.row.tags} cutLength={5} tooltipThreshold={5} />
          ) : (
            <span>-</span>
          ),
      },
      {
        field: 'daysLeft',
        headerName: 'Expires in',
        flex: 2,
        sortable: true,
        renderCell: (params: GridRenderCellParams<PriorityQueueGridRow>) => (
          <ExpirationIndicatorChip
            indicator={params.row.expirationIndicator}
            daysLeft={params.row.daysLeft}
          />
        ),
      },
      {
        field: 'phoneNumber',
        headerName: 'Phone number',
        flex: 3,
        // Not one of the backend's sortable columns.
        sortable: false,
      },
      {
        field: 'onboardingFlow',
        headerName: 'Onboarding flow',
        flex: 3,
        sortable: false,
        renderCell: (params: GridRenderCellParams<PriorityQueueGridRow>) =>
          params.row.onboardingFlow ? (
            <LimitedStringChip text={params.row.onboardingFlow} />
          ) : (
            <span>-</span>
          ),
      },
      {
        field: 'lastExternalContact',
        headerName: 'Last external contact',
        flex: 3,
        sortable: true,
        renderCell: (params: GridRenderCellParams<PriorityQueueGridRow>) => (
          <LastContactCell
            lastContact={
              params.row.lastExternalContact
                ? {
                    channel: params.row.lastExternalContact.channel,
                    daysAgo: getDaysAgo(params.row.lastExternalContact.at),
                  }
                : null
            }
          />
        ),
      },
    ],
    [],
  )

  const {
    columnVisibilityModel,
    pinnedColumns,
    orderedColumns,
    onColumnVisibilityModelChange,
    onPinnedColumnsChange,
    onColumnOrderChange,
    onToggleColumnVisibility,
    onPinColumn,
    onUnpinColumn,
  } = useGridColumnPreferences({
    tableKey: 'monitoring-priority-queue',
    columns,
    lockedColumns: LOCKED_COLUMNS,
  })

  /**
   * What the CSV will contain: the columns currently visible in the grid, in the user's own order,
   * minus any the export endpoint has no key for. Mirrors `ContractorsTable`, so the modal's
   * "Selected columns" list is exactly what lands in the file.
   */
  const exportColumns = useMemo(() => {
    return orderedColumns
      .filter((column) => {
        return EXPORT_COLUMN_KEY[column.field] && columnVisibilityModel[column.field] !== false
      })
      .map((column) => {
        return { field: column.field, headerName: column.headerName || column.field }
      })
  }, [orderedColumns, columnVisibilityModel])

  /** The active filters, spelled the way the modal shows them back to the user before exporting. */
  const exportFilters = useMemo(() => {
    const filters: Array<{ label: string; value: string }> = []

    if (searchQuery) {
      filters.push({ label: 'Search', value: searchQuery })
    }

    if (statusFilter.length) {
      filters.push({
        label: 'Status',
        value: statusFilter.map((status) => contractorStatusTitles[status]).join(', '),
      })
    }

    if (flowFilter.length) {
      const templateNames = flowFilter.map((templateId) => {
        return filterOptions.templates.find((item) => item.id === templateId)?.name || templateId
      })

      filters.push({ label: 'Onboarding flow', value: templateNames.join(', ') })
    }

    if (tagFilter.length) {
      filters.push({ label: 'Tags', value: tagFilter.join(', ') })
    }

    if (expirationFilter.length) {
      filters.push({
        label: 'Expiration',
        value: expirationFilter.map((indicator) => EXPIRATION_FILTER_LABELS[indicator]).join(', '),
      })
    }

    if (excludeWindows[0]) {
      const window = CONTACT_WINDOWS.find((item) => item.value === excludeWindows[0])

      filters.push({ label: 'Exclude contacted', value: window?.label || excludeWindows[0] })
    }

    return filters
  }, [
    searchQuery,
    statusFilter,
    flowFilter,
    tagFilter,
    expirationFilter,
    excludeWindows,
    filterOptions.templates,
  ])

  /**
   * Throws on failure rather than toasting: `ExportDataModal` owns the submitting state, the
   * success/error toast and the Sentry capture, the same way the contractors and onboarding
   * tables wire theirs up.
   */
  const handleExportPriorityQueue = useCallback(
    async (count: number) => {
      const params: ExportPriorityQueueCsvPayload = {
        limit: count,
        exportColumns: exportColumns.map((column) => ({
          key: EXPORT_COLUMN_KEY[column.field],
          header: column.headerName,
        })),
      }

      if (searchQuery) {
        params.search = searchQuery
      }

      if (statusFilter.length) {
        params.contractorStatus = statusFilter
      }

      if (flowFilter.length) {
        params.collectDataTemplateIds = flowFilter
      }

      if (tagFilter.length) {
        params.tags = tagFilter
      }

      if (expirationFilter.length) {
        params.expirationIndicator = expirationFilter
      }

      if (excludeWindows[0]) {
        params.excludeContactedWithin = excludeWindows[0]
      }

      if (sortModel[0]) {
        params.sort = `${SORT_QUERY_KEY[sortModel[0].field]}:${sortModel[0].sort?.toUpperCase()}`
      }

      const buffer: ArrayBuffer = await createAsyncAction(
        dispatch,
        ExportPriorityQueueCsv.request(params),
      )

      if (!buffer || buffer.byteLength === 0) {
        throw new Error('Empty export buffer')
      }

      downloadFileFromBuffer(buffer, 'priority-queue.csv', 'text/csv')
    },
    [
      dispatch,
      exportColumns,
      searchQuery,
      statusFilter,
      flowFilter,
      tagFilter,
      expirationFilter,
      excludeWindows,
      sortModel,
    ],
  )

  const isFiltered = Boolean(
    searchQuery ||
      statusFilter.length ||
      flowFilter.length ||
      tagFilter.length ||
      expirationFilter.length ||
      excludeWindows.length,
  )

  const EmptyElement = useCallback(() => {
    if (hasError) {
      return (
        <ExpirationsEmptyState
          isFiltered
          title="Something went wrong"
          description="We couldn't load the priority queue. Please try again."
        />
      )
    }

    return (
      <ExpirationsEmptyState
        isFiltered={isFiltered}
        title={isFiltered ? 'No contractors found' : 'Queue is clear'}
        description={
          isFiltered
            ? 'Please try a different search query or filters.'
            : 'Nobody needs chasing in the selected period.'
        }
      />
    )
  }, [hasError, isFiltered])

  return (
    <div css={tw`flex flex-col flex-1 min-h-0`}>
      <div
        css={tw`flex flex-row items-center px-4 py-3 bg-white rounded-t-lg border-0 border-b border-solid border-[#EDEDED]`}
      >
        <div css={tw`flex flex-row items-center gap-2`}>
          <GridColumnsSelect
            columnsConfig={columns}
            columnVisibilityModel={columnVisibilityModel}
            pinnedColumns={pinnedColumns}
            lockedColumns={LOCKED_COLUMNS}
            onToggleColumnVisibility={onToggleColumnVisibility}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            onPinColumn={onPinColumn}
            onUnpinColumn={onUnpinColumn}
          />
          <TableSearch
            searchQuery={searchQuery}
            onChangeSearchQuery={handleChangeSearchQuery}
            inputDataTestId="priority-queue-search-input"
            clearButtonDataTestId="priority-queue-search-clear-btn"
          />
          <TableSelect<ContractorStatus>
            title="Status"
            selectedValues={statusFilter}
            icon={
              <GSIcon
                iconName="gs_circle_check_2"
                width={20}
                height={20}
                color={colors.GRAY_BLACK}
              />
            }
            options={STATUS_OPTIONS}
            onSelect={handleChangeStatusFilter}
            triggerDataTestId="priority-queue-status-menu-btn"
            clearAllDataTestId="priority-queue-status-clear-all"
          />
          {/* Matches `TableAssignedTemplateSelect` upstream: searchable menu, chip-rendered
              options, 258px wide. Options come from `filter-options`, so the list only ever
              offers templates actually represented in the queue. */}
          <TableSelect<string>
            title="Assigned Template"
            selectedValues={flowFilter}
            icon={
              <GSIcon iconName="gs_clipboard" width={20} height={20} color={colors.GRAY_BLACK} />
            }
            options={filterOptions.templates.map((template) => ({
              value: template.id,
              label: template.name,
              component: <LimitedStringChip text={template.name} />,
            }))}
            searchable
            searchPlaceholder="Search"
            menuWidth={258}
            emptyText="No templates in the queue"
            onSelect={handleChangeFlowFilter}
            triggerDataTestId="priority-queue-template-menu-btn"
            clearAllDataTestId="priority-queue-template-clear-all"
            searchInputDataTestId="priority-queue-template-search-input"
          />
          <TableSelect<string>
            title="Tags"
            selectedValues={tagFilter}
            icon={<GSIcon iconName="gs_tag_2" width={20} height={20} color={colors.GRAY_BLACK} />}
            options={filterOptions.tags.map((tag) => ({
              value: tag,
              component: <Typography variant="subtitle1">{tag}</Typography>,
            }))}
            emptyText="No tags in the queue"
            onSelect={handleChangeTagFilter}
            triggerDataTestId="priority-queue-tag-menu-btn"
            clearAllDataTestId="priority-queue-tag-clear-all"
          />
          <TableSelect<ExpirationIndicator>
            title="Expires"
            selectedValues={expirationFilter}
            icon={<GSIcon iconName="gs_clock" width={20} height={20} color={colors.GRAY_BLACK} />}
            options={EXPIRATION_OPTIONS}
            onSelect={handleChangeExpirationFilter}
            triggerDataTestId="priority-queue-expiration-menu-btn"
            clearAllDataTestId="priority-queue-expiration-clear-all"
          />
          <TableSelect<ExcludeContactedWindow>
            title="Exclude contacted"
            selectedValues={excludeWindows}
            icon={
              <GSIcon iconName="gs_clock_snooze" width={20} height={20} color={colors.GRAY_BLACK} />
            }
            options={CONTACT_WINDOWS.map((item) => ({
              value: item.value,
              component: <Typography variant="subtitle1">{item.label}</Typography>,
            }))}
            singleSelect
            onSelect={handleChangeExcludeWindows}
            triggerDataTestId="priority-queue-contacted-menu-btn"
            clearAllDataTestId="priority-queue-contacted-clear-all"
          />
        </div>
      </div>

      <GridTable<PriorityQueueGridRow>
        className={tw`flex-1 min-h-0 overflow-hidden rounded-b-lg`}
        rows={rows}
        isLoading={isLoading}
        columns={orderedColumns}
        getRowId={(row: PriorityQueueGridRow) => row.id}
        columnVisibilityModel={columnVisibilityModel}
        pinnedColumns={pinnedColumns}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        onPinnedColumnsChange={onPinnedColumnsChange}
        onColumnOrderChange={onColumnOrderChange}
        EmptyElement={EmptyElement}
        paginationModel={paginationModel}
        onPaginationModelChange={handleChangePagination}
        rowCount={total}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        tableDataTestId="priority-queue-table"
        rowDataTestIdPrefix="priority-queue"
        onRowClick={(params) => setOpenedId(String(params.id))}
        onExportClick={handleOpenExportModal}
      />

      <ContactContractorDrawer
        detail={detail}
        onLogContact={handleLogContact}
        onClose={handleCloseDrawer}
      />

      <ExportDataModal
        isOpen={isExportModalOpen}
        onClose={handleCloseExportModal}
        entityNamePlural="contractors"
        columns={exportColumns}
        filters={exportFilters}
        defaultCount={Math.min(total, limit)}
        maxCount={total}
        onExport={handleExportPriorityQueue}
      />
    </div>
  )
}

export const PriorityQueue = memo(PriorityQueueComponent)
